import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import type { DeleteEntityTextElements } from "~/components/DeleteEntityView";
import DeleteEntityView from "~/components/DeleteEntityView";
import { CLIENT_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { deleteCentralInvoiceRecipient } from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientApiDispatchers.ts";
import { centralInvoiceRecipientQueries } from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientQueries.ts";


type Props = {
	clientId: string;
	invoiceRecipientDisplayName: string;
	invoiceRecipientId: string | null;
	isOpen: boolean;
	onClose: () => void;
};

const DeleteCentralInvoiceRecipientModal = ({
	clientId,
	invoiceRecipientDisplayName,
	invoiceRecipientId,
	onClose,
	isOpen,
}: Props) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	useEffect(() => {
		if (isOpen) {
			setError("");
		}
	}, [isOpen]);

	const handleConfirmationClick = async () => {
		if (!invoiceRecipientId) return;
		try {
			setBusy(true);
			await deleteCentralInvoiceRecipient({ clientId, invoiceRecipientId });
			await queryClient.invalidateQueries({ queryKey: centralInvoiceRecipientQueries.byClient(clientId).queryKey });
			navigate(generatePath(CLIENT_DETAILS_PATH, { clientId }));
		} catch (error) {
			const errorData = handleApiError(error);
			if (errorData.type === ApiResponseErrorType.VALIDATION) {
				if (
					errorData.messages.invoiceRecipientId?.find(
						(validationError) => validationError.rule === "invoiceRecipientIsDeletableRule",
					)
				) {
					setError("Dieser Rechnungsempfänger kann nicht gelöscht werden, da er noch in Verwendung ist.");
				}
			} else {
				setError("Ein unbekannter Fehler ist aufgetreten.");
			}
		}
		setBusy(false);
	};

	const texts: DeleteEntityTextElements = {
		headerText: `Rechnungsempfänger löschen`,
		bodyText: `Möchtest Du den Rechnungsempfänger "${invoiceRecipientDisplayName}" wirklich löschen?`,
		deleteButtonText: `Rechnungsempfänger löschen`,
	};

	return (
		<DeleteEntityView
			error={error}
			texts={texts}
			isOpen={isOpen}
			onCloseClick={onClose}
			busy={busy}
			handleConfirmationClick={handleConfirmationClick}
		/>
	);
};

export default DeleteCentralInvoiceRecipientModal;
